import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Search, Trash2, Pencil, RefreshCcw, X, ListTodo, Check } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import EmptyState from "@/components/admin/EmptyState";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

// Convert ISO string to <input type="datetime-local"> value (local time)
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

export default function AdminReminders() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("open"); // open | completed | all
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter === "completed") params.completed = true;
      if (statusFilter === "open") params.completed = false;
      const res = await adminApi.listReminders(params);
      setRows(res);
    } catch {
      toast.error("Couldn't load reminders.");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (form) => {
    try {
      const payload = {
        title: form.title.trim(),
        notes: form.notes.trim(),
        due_at: localInputToIso(form.due_at),
      };
      if (modal.mode === "create") {
        const created = await adminApi.createReminder(payload);
        setRows((prev) => [created, ...prev]);
        toast.success("Reminder added.");
      } else {
        const updated = await adminApi.updateReminder(modal.data.id, payload);
        setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        toast.success("Reminder updated.");
      }
      setModal(null);
    } catch {
      toast.error("Couldn't save.");
    }
  };

  const toggleComplete = async (row) => {
    try {
      const updated = await adminApi.updateReminder(row.id, { completed: !row.completed });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch {
      toast.error("Couldn't update.");
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      await adminApi.deleteReminder(confirm.id);
      setRows((prev) => prev.filter((r) => r.id !== confirm.id));
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete.");
    } finally {
      setConfirm(null);
    }
  };

  return (
    <div data-testid="page-admin-reminders" className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#6E6759" }} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title or notes…"
            data-testid="reminders-search"
            className="w-full rounded-full border pl-10 pr-4 py-2 text-sm outline-none"
            style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)", color: "#2A2621" }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="reminders-status-filter"
          className="rounded-full border px-4 py-2 text-sm"
          style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)", color: "#2A2621" }}
        >
          <option value="open">Open</option>
          <option value="completed">Completed</option>
          <option value="all">All</option>
        </select>
        <button type="button" onClick={load} className="btn-outline-forest" data-testid="reminders-refresh">
          <RefreshCcw size={14} /> Refresh
        </button>
        <button
          type="button"
          onClick={() => setModal({ mode: "create", data: { title: "", notes: "", due_at: isoToLocalInput(new Date().toISOString()) } })}
          className="btn-gold ml-auto"
          data-testid="reminders-new"
        >
          <Plus size={14} /> New reminder
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-3" style={{ color: "#1B4332" }}>
          <Loader2 className="animate-spin" size={16} /> Loading&hellip;
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ListTodo size={20} />}
          title="Nothing on the list"
          subtitle="Add a reminder to keep gentle track of follow-ups, deadlines, or personal notes."
          testid="reminders-empty"
        />
      ) : (
        <div className="grid gap-4">
          {rows.map((row) => (
            <div
              key={row.id}
              className="rounded-2xl border p-5"
              style={{
                background: "#fff",
                borderColor: "rgba(27,67,50,0.12)",
                opacity: row.completed ? 0.7 : 1,
              }}
              data-testid={`reminder-row-${row.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-serif text-lg"
                      style={{
                        color: "#1B4332",
                        textDecoration: row.completed ? "line-through" : "none",
                      }}
                    >
                      {row.title}
                    </span>
                    {row.completed && (
                      <span
                        className="text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full"
                        style={{ background: "#1B4332", color: "#C9A84C" }}
                      >
                        Done
                      </span>
                    )}
                  </div>
                  {row.notes && <p className="mt-2 text-sm leading-relaxed" style={{ color: "#2A2621" }}>{row.notes}</p>}
                  <div className="mt-3 text-xs" style={{ color: "#C9A84C" }}>Due {formatDate(row.due_at)}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleComplete(row)}
                    className="btn-outline-forest"
                    data-testid={`reminder-toggle-${row.id}`}
                  >
                    <Check size={14} /> {row.completed ? "Reopen" : "Complete"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setModal({ mode: "edit", data: { ...row, due_at: isoToLocalInput(row.due_at) } })}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border"
                    style={{ borderColor: "rgba(27,67,50,0.2)", color: "#1B4332" }}
                    data-testid={`reminder-edit-${row.id}`}
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm({ id: row.id, label: row.title })}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border"
                    style={{ borderColor: "#8A2A2A", color: "#8A2A2A" }}
                    data-testid={`reminder-delete-${row.id}`}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ReminderModal
          mode={modal.mode}
          initial={modal.data}
          onClose={() => setModal(null)}
          onSubmit={save}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title="Delete reminder?"
        message={confirm ? `“${confirm.label}” will be removed permanently.` : ""}
        confirmLabel="Delete"
        destructive
        onClose={() => setConfirm(null)}
        onConfirm={doDelete}
        testid="reminders-confirm-delete"
      />
    </div>
  );
}

function ReminderModal({ mode, initial, onClose, onSubmit }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    notes: initial?.notes || "",
    due_at: initial?.due_at || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.due_at) {
      toast.error("Title and due date are required.");
      return;
    }
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="reminder-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-lg rounded-2xl border p-7"
        style={{ background: "#FAF6EC", borderColor: "rgba(27,67,50,0.15)", boxShadow: "0 20px 60px -20px rgba(27,67,50,0.5)" }}
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5" aria-label="Close">
          <X size={14} />
        </button>
        <h3 className="font-serif text-2xl" style={{ color: "#1B4332" }}>
          {mode === "create" ? "New reminder" : "Edit reminder"}
        </h3>
        <div className="mt-5 space-y-4">
          <label className="flex flex-col gap-2">
            <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#1B4332" }}>Title</span>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="reminder-input-title"
              className="rounded-xl border px-4 py-2 text-sm"
              style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)" }}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#1B4332" }}>Notes</span>
            <textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="reminder-input-notes"
              className="rounded-xl border px-4 py-2 text-sm resize-none"
              style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)" }}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#1B4332" }}>Due at</span>
            <input
              type="datetime-local"
              value={form.due_at}
              onChange={(e) => setForm({ ...form, due_at: e.target.value })}
              data-testid="reminder-input-due-at"
              className="rounded-xl border px-4 py-2 text-sm"
              style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)" }}
            />
          </label>
        </div>
        <div className="mt-7 flex flex-wrap gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-outline-forest" data-testid="reminder-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-gold" data-testid="reminder-save">
            {saving ? "Saving…" : mode === "create" ? "Create" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
